import { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function EventDialog({ open, onClose, onSuccess, event = null }) {
  const isEdit = !!event;
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    location: '',
    start_date: '',
    end_date: '',
    setup_days: 0,
    travel_departure_date: '',
    travel_return_date: '',
    load_in_date: '',
    load_in_time: '',
    load_out_date: '',
    load_out_time: '',
    application_status: 'not_applied',
    approved_paid: false,
    staff_working: '',
    trailer_info: '',
    power_electric_needs: '',
    power_cost: 0,
    booth_cost: 0,
    projected_revenue: 0,
    notes: '',
    show_type: 'vyrus_graphics',
    attending_with_partner: false,
    partner_name: '',
  });

  useEffect(() => {
    if (event) {
      setFormData({
        name: event.name,
        location: event.location,
        start_date: event.start_date,
        end_date: event.end_date,
        setup_days: event.setup_days,
        travel_departure_date: event.travel_departure_date || '',
        travel_return_date: event.travel_return_date || '',
        load_in_date: event.load_in_date || '',
        load_in_time: event.load_in_time || '',
        load_out_date: event.load_out_date || '',
        load_out_time: event.load_out_time || '',
        application_status: event.application_status,
        approved_paid: event.approved_paid,
        staff_working: event.staff_working.join(', '),
        trailer_info: event.trailer_info || '',
        power_electric_needs: event.power_electric_needs || '',
        power_cost: event.power_cost,
        booth_cost: event.booth_cost || 0,
        projected_revenue: event.projected_revenue || 0,
        notes: event.notes || '',
        show_type: event.show_type || 'vyrus_graphics',
        attending_with_partner: event.attending_with_partner || false,
        partner_name: event.partner_name || '',
      });
    } else {
      setFormData({
        name: '',
        location: '',
        start_date: '',
        end_date: '',
        setup_days: 0,
        travel_departure_date: '',
        travel_return_date: '',
        load_in_date: '',
        load_in_time: '',
        load_out_date: '',
        load_out_time: '',
        application_status: 'not_applied',
        approved_paid: false,
        staff_working: '',
        trailer_info: '',
        power_electric_needs: '',
        power_cost: 0,
        booth_cost: 0,
        projected_revenue: 0,
        notes: '',
        show_type: 'vyrus_graphics',
        attending_with_partner: false,
        partner_name: '',
      });
    }
  }, [event, open]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('vyrus_token');
      const headers = { Authorization: `Bearer ${token}` };

      const submitData = {
        ...formData,
        setup_days: parseInt(formData.setup_days) || 0,
        power_cost: parseFloat(formData.power_cost) || 0,
        booth_cost: parseFloat(formData.booth_cost) || 0,
        projected_revenue: parseFloat(formData.projected_revenue) || 0,
        staff_working: formData.staff_working
          .split(',')
          .map((s) => s.trim())
          .filter((s) => s),
        attending_with_partner: formData.application_status === 'attending_partner',
        partner_name: formData.application_status === 'attending_partner' ? formData.partner_name : null,
      };

      if (isEdit) {
        await axios.put(`${BACKEND_URL}/api/events/${event.id}`, submitData, { headers });
        toast.success('Event updated successfully');
      } else {
        await axios.post(`${BACKEND_URL}/api/events`, submitData, { headers });
        toast.success('Event created successfully');
      }

      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save event');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-card">
        <DialogHeader>
          <DialogTitle className="text-2xl font-heading">
            {isEdit ? 'Edit Event' : 'Create New Event'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label htmlFor="name">Event Name *</Label>
              <Input
                id="name"
                name="name"
                data-testid="event-name-input"
                value={formData.name}
                onChange={handleChange}
                required
                className="bg-secondary/50 border-transparent focus:border-primary"
              />
            </div>

            <div className="md:col-span-2">
              <Label htmlFor="location">Location (Address) *</Label>
              <Input
                id="location"
                name="location"
                data-testid="event-location-input"
                value={formData.location}
                onChange={handleChange}
                required
                className="bg-secondary/50 border-transparent focus:border-primary"
              />
            </div>

            <div>
              <Label htmlFor="start_date">Start Date *</Label>
              <Input
                id="start_date"
                name="start_date"
                type="date"
                data-testid="event-start-date-input"
                value={formData.start_date}
                onChange={handleChange}
                required
                className="bg-secondary/50 border-transparent focus:border-primary"
              />
            </div>

            <div>
              <Label htmlFor="end_date">End Date *</Label>
              <Input
                id="end_date"
                name="end_date"
                type="date"
                data-testid="event-end-date-input"
                value={formData.end_date}
                onChange={handleChange}
                required
                className="bg-secondary/50 border-transparent focus:border-primary"
              />
            </div>

            <div>
              <Label htmlFor="travel_departure_date">Departure Date</Label>
              <Input
                id="travel_departure_date"
                name="travel_departure_date"
                type="date"
                data-testid="event-departure-date-input"
                value={formData.travel_departure_date}
                onChange={handleChange}
                className="bg-secondary/50 border-transparent focus:border-primary"
              />
            </div>

            <div>
              <Label htmlFor="travel_return_date">Return Date</Label>
              <Input
                id="travel_return_date"
                name="travel_return_date"
                type="date"
                data-testid="event-return-date-input"
                value={formData.travel_return_date}
                onChange={handleChange}
                className="bg-secondary/50 border-transparent focus:border-primary"
              />
            </div>

            <div>
              <Label htmlFor="load_in_date">Load In Date</Label>
              <Input
                id="load_in_date"
                name="load_in_date"
                type="date"
                data-testid="event-load-in-date-input"
                value={formData.load_in_date}
                onChange={handleChange}
                className="bg-secondary/50 border-transparent focus:border-primary"
              />
            </div>

            <div>
              <Label htmlFor="load_in_time">Load In Time</Label>
              <Input
                id="load_in_time"
                name="load_in_time"
                type="time"
                data-testid="event-load-in-time-input"
                value={formData.load_in_time}
                onChange={handleChange}
                className="bg-secondary/50 border-transparent focus:border-primary"
              />
            </div>

            <div>
              <Label htmlFor="load_out_date">Load Out Date</Label>
              <Input
                id="load_out_date"
                name="load_out_date"
                type="date"
                data-testid="event-load-out-date-input"
                value={formData.load_out_date}
                onChange={handleChange}
                className="bg-secondary/50 border-transparent focus:border-primary"
              />
            </div>

            <div>
              <Label htmlFor="load_out_time">Load Out Time</Label>
              <Input
                id="load_out_time"
                name="load_out_time"
                type="time"
                data-testid="event-load-out-time-input"
                value={formData.load_out_time}
                onChange={handleChange}
                className="bg-secondary/50 border-transparent focus:border-primary"
              />
            </div>

            <div>
              <Label htmlFor="setup_days">Setup Days</Label>
              <Input
                id="setup_days"
                name="setup_days"
                type="number"
                data-testid="event-setup-days-input"
                value={formData.setup_days}
                onChange={handleChange}
                className="bg-secondary/50 border-transparent focus:border-primary"
              />
            </div>

            <div>
              <Label htmlFor="application_status">Application Status *</Label>
              <select
                id="application_status"
                name="application_status"
                data-testid="event-status-select"
                value={formData.application_status}
                onChange={handleChange}
                className="w-full px-3 py-2 rounded-lg bg-secondary/50 border border-transparent focus:border-primary text-foreground focus:outline-none focus:ring-1 focus:ring-primary h-11"
              >
                <option value="not_applied">Not Applied</option>
                <option value="applied">Applied</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
                <option value="sold_out">Sold Out</option>
                <option value="attending_partner">Attending with Partner</option>
              </select>
            </div>

            {formData.application_status === 'attending_partner' && (
              <div>
                <Label htmlFor="partner_name">Partner/Business Name *</Label>
                <Input
                  id="partner_name"
                  name="partner_name"
                  data-testid="event-partner-name-input"
                  placeholder="Enter partner or business name"
                  value={formData.partner_name}
                  onChange={handleChange}
                  className="bg-secondary/50 border-transparent focus:border-primary"
                />
              </div>
            )}

            <div>
              <Label htmlFor="show_type">Show Type *</Label>
              <select
                id="show_type"
                name="show_type"
                data-testid="event-show-type-select"
                value={formData.show_type}
                onChange={handleChange}
                className="w-full px-3 py-2 rounded-lg bg-secondary/50 border border-transparent focus:border-primary text-foreground focus:outline-none focus:ring-1 focus:ring-primary h-11"
              >
                <option value="vyrus_graphics">Vyrus Graphics (Big Show)</option>
                <option value="vyrus_trading_cards">Vyrus Trading Cards (Small Show)</option>
              </select>
            </div>

            <div className="flex items-center space-x-2 md:col-span-2">
              <input
                id="approved_paid"
                name="approved_paid"
                type="checkbox"
                data-testid="event-approved-paid-checkbox"
                checked={formData.approved_paid}
                onChange={handleChange}
                className="h-4 w-4 rounded border-border bg-secondary text-primary focus:ring-primary"
              />
              <Label htmlFor="approved_paid" className="cursor-pointer">
                Approved & Paid
              </Label>
            </div>

            <div>
              <Label htmlFor="power_cost">Power/Electric Cost</Label>
              <Input
                id="power_cost"
                name="power_cost"
                type="number"
                step="0.01"
                data-testid="event-power-cost-input"
                value={formData.power_cost}
                onChange={handleChange}
                className="bg-secondary/50 border-transparent focus:border-primary"
              />
            </div>

            <div>
              <Label htmlFor="booth_cost">Booth/Table Cost</Label>
              <Input
                id="booth_cost"
                name="booth_cost"
                type="number"
                step="0.01"
                data-testid="event-booth-cost-input"
                value={formData.booth_cost}
                onChange={handleChange}
                className="bg-secondary/50 border-transparent focus:border-primary"
              />
            </div>

            <div className="md:col-span-2">
              <Label htmlFor="projected_revenue">Projected Revenue (Estimate)</Label>
              <Input
                id="projected_revenue"
                name="projected_revenue"
                type="number"
                step="0.01"
                data-testid="event-projected-revenue-input"
                value={formData.projected_revenue}
                onChange={handleChange}
                className="bg-secondary/50 border-transparent focus:border-primary"
              />
            </div>

            <div>
              <Label htmlFor="power_electric_needs">Power/Electric Needs</Label>
              <Input
                id="power_electric_needs"
                name="power_electric_needs"
                data-testid="event-power-needs-input"
                value={formData.power_electric_needs}
                onChange={handleChange}
                className="bg-secondary/50 border-transparent focus:border-primary"
              />
            </div>

            <div className="md:col-span-2">
              <Label htmlFor="trailer_info">Trailer Info</Label>
              <Input
                id="trailer_info"
                name="trailer_info"
                data-testid="event-trailer-input"
                value={formData.trailer_info}
                onChange={handleChange}
                className="bg-secondary/50 border-transparent focus:border-primary"
              />
            </div>

            <div className="md:col-span-2">
              <Label htmlFor="staff_working">Staff Working (comma-separated)</Label>
              <Input
                id="staff_working"
                name="staff_working"
                data-testid="event-staff-input"
                placeholder="John Doe, Jane Smith"
                value={formData.staff_working}
                onChange={handleChange}
                className="bg-secondary/50 border-transparent focus:border-primary"
              />
            </div>

            <div className="md:col-span-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                name="notes"
                data-testid="event-notes-input"
                value={formData.notes}
                onChange={handleChange}
                rows={3}
                className="bg-secondary/50 border-transparent focus:border-primary"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              data-testid="event-cancel-button"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              data-testid="event-submit-button"
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              {loading ? 'Saving...' : isEdit ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
